import { company, products } from '../data/site'
import { pageMeta as cadreMeta } from '../data/cadre-management'
import { pageMeta as partyMeta } from '../data/smart-party'
import { pageMeta as villageMeta } from '../data/digital-village'
import { pageMeta as governanceMeta } from '../data/comprehensive-governance'
import { pageMeta as inspectionMeta } from '../data/smart-inspection'

export const SITE_URL = (process.env.VUE_APP_SITE_URL || 'https://www.example.com').replace(/\/$/, '')

const productSummary = products.map((p) => p.title).join('、')

export const routeSeoConfig = {
  '/': {
    title: '十堰小智科技_智慧政务解决方案_数字治理平台',
    description: company.description,
    keywords: '十堰小智科技,智慧政务,数字治理,AI解决方案,数字化转型,十堰,政务信息化',
    canonical: '/'
  },
  '/about': {
    title: '关于我们_十堰小智科技有限公司',
    description: `${company.fullName}专注智慧政务与数字化治理，为各级政府部门提供干部管理、智慧党建、数字乡村、综合治理、智慧巡检等一站式解决方案。`,
    keywords: '十堰小智科技,关于我们,智慧政务,公司简介,十堰科技公司',
    canonical: '/about'
  },
  '/contact': {
    title: '联系我们_十堰小智科技',
    description: `联系${company.fullName}：地址${company.address}，电话${company.phone}，邮箱${company.email}。欢迎预约产品演示与方案咨询。`,
    keywords: '十堰小智科技,联系我们,预约演示,智慧政务咨询,十堰',
    canonical: '/contact'
  },
  '/products': {
    title: '核心产品_智慧政务解决方案_小智科技',
    description: `${company.fullName}核心产品包括${productSummary}，覆盖组织人事、基层党建、乡村治理、基层综治与智慧巡检等场景。`,
    keywords: '智慧政务产品,数字干部管理,智慧党建,数字乡村,综合治理,智慧巡检,小智科技',
    canonical: '/products'
  },
  '/cadre-management': {
    title: '数字干部管理系统_小智科技',
    description: cadreMeta.description,
    keywords: '数字干部管理,干部管理系统,组织人事,干部考核,小智科技,十堰',
    canonical: '/cadre-management',
    productName: cadreMeta.title
  },
  '/smart-party': {
    title: '智慧党建管理系统_小智科技',
    description: partyMeta.description,
    keywords: '智慧党建,党建管理系统,党员管理,组织生活,小智科技',
    canonical: '/smart-party',
    productName: partyMeta.title
  },
  '/digital-village': {
    title: '数字乡村管理系统_小智科技',
    description: villageMeta.description,
    keywords: '数字乡村,乡村治理,智慧农业,乡村振兴,小智科技',
    canonical: '/digital-village',
    productName: villageMeta.title
  },
  '/comprehensive-governance': {
    title: '综合治理管理系统_小智科技',
    description: governanceMeta.description,
    keywords: '综合治理,网格化管理,基层治理,矛盾调解,小智科技',
    canonical: '/comprehensive-governance',
    productName: governanceMeta.title
  },
  '/smart-inspection': {
    title: '智慧巡检管理系统_小智科技',
    description: inspectionMeta.description,
    keywords: '智慧巡检,AI巡检,无人机巡检,视频监控,小智科技',
    canonical: '/smart-inspection',
    productName: inspectionMeta.title
  },
  '/404': {
    title: '页面未找到_小智科技',
    description: `${company.fullName}官网 - 您访问的页面不存在，请返回首页浏览智慧政务产品与解决方案。`,
    keywords: '十堰小智科技,智慧政务',
    canonical: '/404'
  }
}

export const PRERENDER_ROUTES = [
  '/',
  '/about',
  '/contact',
  '/products',
  '/cadre-management',
  '/smart-party',
  '/digital-village',
  '/comprehensive-governance',
  '/smart-inspection'
]

export const SITEMAP_ROUTES = PRERENDER_ROUTES.map((loc, index) => {
  if (loc === '/') return { loc, changefreq: 'weekly', priority: '1.0' }
  if (['/about', '/contact', '/products'].includes(loc)) {
    return { loc, changefreq: 'monthly', priority: '0.7' }
  }
  return { loc, changefreq: 'monthly', priority: '0.9' }
})

const PRODUCT_ROUTES = new Set([
  '/cadre-management',
  '/smart-party',
  '/digital-village',
  '/comprehensive-governance',
  '/smart-inspection'
])

export function absoluteUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalized}`
}

export function getRouteSeo(route) {
  const config = routeSeoConfig[route.path] || routeSeoConfig['/404']
  return { ...config, path: route.path }
}

export function buildMetaInfo(seo) {
  const url = absoluteUrl(seo.canonical || seo.path || '/')
  return {
    title: seo.title,
    meta: [
      { name: 'description', content: seo.description },
      { name: 'keywords', content: seo.keywords },
      { property: 'og:title', content: seo.title },
      { property: 'og:description', content: seo.description },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: company.fullName },
      { property: 'og:locale', content: 'zh_CN' }
    ],
    link: [{ rel: 'canonical', href: url }]
  }
}

export function buildStructuredData(route, seo) {
  const scripts = [
    {
      type: 'application/ld+json',
      json: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: company.fullName,
        url: SITE_URL,
        logo: absoluteUrl('/favicon.svg'),
        description: company.description,
        address: {
          '@type': 'PostalAddress',
          streetAddress: company.address,
          addressLocality: '十堰市',
          addressRegion: '湖北省',
          addressCountry: 'CN'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: `+86-${company.phone}`,
          email: company.email,
          contactType: 'customer service',
          areaServed: 'CN',
          availableLanguage: 'Chinese'
        }
      }
    }
  ]

  if (route.path === '/') {
    scripts.push({
      type: 'application/ld+json',
      json: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: company.fullName,
        url: SITE_URL,
        description: company.description,
        publisher: {
          '@type': 'Organization',
          name: company.fullName
        }
      }
    })
  }

  if (PRODUCT_ROUTES.has(route.path) && seo.productName) {
    scripts.push({
      type: 'application/ld+json',
      json: {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: seo.productName,
        description: seo.description,
        url: absoluteUrl(route.path),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'CNY',
          availability: 'https://schema.org/InStock'
        },
        provider: {
          '@type': 'Organization',
          name: company.fullName,
          url: SITE_URL
        }
      }
    })
  }

  return scripts
}
